import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import HitCard from 'Components/Card/HitCard/HitCard';
import ViewAllCard from 'Components/Carousel/ProductCardCarousel/ViewAllCard';
import SkeletonHitCard from 'Components/Card/HitCard/SkeletonHitCard';
import SiteSlider from 'Components/Slider/SiteSlider';
import useSliderImageSrcSizes from 'Hooks/SiteSlider/useSliderImageSrcSizes';

/**
 * @param {Array.object} hitData - Array of products to show as HitCards.
 * @param {Object} sliderSettings - Settings to customise the slider. If none are provided, defualts below will be used.
 * @param {string} place - Where the ProductCarousel is placed on within the website.
 * @param {boolean} sliderLightTheme - Boolean to control whether to show light theme cards.
 * @param {number} slidesToShow - How many cards to show in one slide of the carousel on XL & XXL breakpoints.
 * @param {string} viewAllLocation - Path which the last item in the carousel should link to.
 * @param {string} viewAllTitle - Title for the last item in the carousel.
 * @param {boolean} showFlashDealBar - Boolean to control whether to show the flash deal countdown bar on the bottom of the HitCard.
 * @param {boolean} showFakeCards - Boolean to control whether to show skeleton HitCards if the hitData is empty.
 * @param {boolean} lazyLoadCover - Boolean to control whether to lazy load the product cover, this is true by default.
 * @param {string} hitCardNameTag - This is a div by default but can be set to a heading tag such as h2, this can help semantic html markup on product lists.
 * @param {string} carouselCoverSize - This tells the card what size the cover is so it can show portrait covers etc.
*/

const ProductCardCarousel = React.memo(({
  hitData,
  sliderSettings,
  sliderLightTheme,
  slidesToShow,
  place,
  viewAllLocation,
  viewAllTitle,
  showFlashDealBar,
  showFakeCards,
  lazyLoadCover,
  hitCardNameTag,
  carouselCoverSize,
}) => {
  const sliderSrcSizes = useSliderImageSrcSizes(sliderSettings, (hitData || []).length);

  if (!hitData && !showFakeCards) return false;

  if (isEmpty(hitData) && showFakeCards) {
    return (
      <SiteSlider sliderLightTheme={sliderLightTheme} sliderSettings={sliderSettings} sliderClass="ProductCardCarousel">
        {[...Array(12).keys()].map(fakeHit =>
          <SkeletonHitCard
            key={fakeHit + 1}
            numberOfCards={1}
            xsSize={12}
            smSize={12}
            mdSize={12}
            lgSize={12}
            showFlashDealBar={showFlashDealBar}
            coverType={carouselCoverSize}
            lightThemeOverride={sliderLightTheme}
          />,
        )}
      </SiteSlider>
    );
  }

  let hitDataLength = hitData.length;
  if (viewAllLocation && viewAllLocation !== 'wishlist' && hitDataLength >= (slidesToShow * 3)) (hitDataLength -= 1);
  if (viewAllLocation && viewAllLocation === 'wishlist' && hitDataLength === 15) (hitDataLength -= 1);

  let showViewAllCard = false;
  if (viewAllLocation && viewAllLocation !== '') showViewAllCard = true;

  return (
    <SiteSlider sliderLightTheme={sliderLightTheme} sliderSettings={sliderSettings} sliderClass="ProductCardCarousel">
      {hitData.slice(0, hitDataLength).map(hit =>
        <HitCard
          key={hit.product_id}
          showFlashDealBar={showFlashDealBar}
          hitCardLightTheme={sliderLightTheme}
          xsSize={12}
          smSize={12}
          mdSize={12}
          lgSize={12}
          sizesOverride={sliderSrcSizes}
          hit={hit}
          place={place}
          lazy={lazyLoadCover}
          hitCardNameTag={hitCardNameTag}
          coverTypeOverride={carouselCoverSize}
          adaptiveSize={carouselCoverSize === 'portrait' || carouselCoverSize === 'square'}
        />,
      )}
      {showViewAllCard &&
        <ViewAllCard
          key={`view-all-${viewAllLocation}`}
          title={viewAllTitle}
          location={viewAllLocation}
          cardAspectSize={carouselCoverSize}
          showFlashDealBar={showFlashDealBar}
        />
      }
    </SiteSlider>
  );
});

ProductCardCarousel.propTypes = {
  hitData: PropTypes.arrayOf(PropTypes.shape),
  sliderLightTheme: PropTypes.bool,
  slidesToShow: PropTypes.number,
  place: PropTypes.string,
  viewAllLocation: PropTypes.string,
  viewAllTitle: PropTypes.string,
  showFlashDealBar: PropTypes.bool,
  showFakeCards: PropTypes.bool,
  lazyLoadCover: PropTypes.bool,
  hitCardNameTag: PropTypes.string,
  carouselCoverSize: PropTypes.oneOf([
    '',
    '16x9',
    'portrait',
    'square',
  ]),
  sliderSettings: PropTypes.shape({}),
};

ProductCardCarousel.defaultProps = {
  hitData: [],
  sliderLightTheme: false,
  place: '',
  slidesToShow: 3,
  viewAllLocation: '',
  viewAllTitle: '',
  showFlashDealBar: false,
  showFakeCards: false,
  lazyLoadCover: true,
  hitCardNameTag: 'div',
  carouselCoverSize: '',
  sliderSettings: {},
};

export default ProductCardCarousel;
